// SurveyMapView.js
import React, { useEffect, useRef, useState } from "react";
import { GoogleMap, Marker, Polygon, InfoWindow } from "@react-google-maps/api";
import { Button, Modal } from "antd";
import AddStateShape from "../../SurveyAnalytics/AddStateShape";
import SurveyAnalyticsDashboard from "./SurveyAnalyticsDasboard/SurveyAnalyticsDashboard";
import useAxiosInstance from "../../../lib/useAxiosInstance";

const SurveyMapView = ({
  isLoaded,
  open,
  setIsFullScreen,
  id,
  center,
  polygonPaths = [],
  mapRef,
  survey
}) => {
  const axiosInstance = useAxiosInstance();
  // Internal map ref
  const internalRef = useRef(null);
  const mapRefToUse = mapRef || internalRef;
  const [legendColors, setLegendColors] = useState([]);
  const [selectedPolygon, setSelectedPolygon] = useState(null);
  // State for AddStateShape popup
  const [showStateShape, setShowStateShape] = useState(false);
  const [selectedLocationName, setSelectedLocationName] = useState(null);
  

  // Lock body scroll when open full-screen
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  const handlePolygonLoad = (polygonInstance) => {
    polygonInstance.setMap(mapRefToUse.current);
  };

  const fitToPolygons = (mapInstance, polygons, fallbackCenter) => {
    if (
      !window.google ||
      !mapInstance ||
      !polygons ||
      !Array.isArray(polygons) ||
      polygons.length === 0
    ) {
      if (fallbackCenter) {
        mapInstance?.setCenter(fallbackCenter);
        mapInstance?.setZoom(10);
      }
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    polygons.forEach((polygon) => {
      polygon?.coordinate?.forEach((point) => {
        const lat = Number(point?.lat);
        const lng = Number(point?.lng);
        if (!isNaN(lat) && !isNaN(lng)) bounds.extend({ lat, lng });
      });
    });

    if (!bounds.isEmpty()) {
      mapInstance.fitBounds(bounds);
      mapInstance.setCenter(bounds.getCenter());
    } else if (fallbackCenter) {
      mapInstance.setCenter(fallbackCenter);
      mapInstance.setZoom(10);
    }
  };
  // Clear filter
  useEffect(() => {
  // If filter is cleared from dashboard, clear polygon
  if (!selectedLocationName && selectedPolygon) {
    setSelectedPolygon(null);

    if (mapRefToUse.current && polygonPaths?.length) {
      const bounds = new window.google.maps.LatLngBounds();
      polygonPaths.forEach((poly) =>
        poly.coordinate?.forEach((p) =>
          bounds.extend({ lat: Number(p.lat), lng: Number(p.lng) })
        )
      );
      mapRefToUse.current.fitBounds(bounds);
    }
  }
}, [selectedLocationName]);

  useEffect(() => {
    if (!selectedLocationName || !polygonPaths?.length) return;

    const match = polygonPaths.find(
      (p) =>
        p.name?.toString().trim().toUpperCase() ===
        selectedLocationName.toString().trim().toUpperCase()
    );

    if (match) {
      handlePolygonClick(match);
    }
  }, [selectedLocationName, polygonPaths]);

  // Auto focus polygons on load or update
  useEffect(() => {
    if (!isLoaded || !window.google || !mapRefToUse.current) return;

    const timer = setTimeout(() => {
      fitToPolygons(mapRefToUse.current, polygonPaths, center);
    }, 100);

    return () => clearTimeout(timer);
  }, [polygonPaths, isLoaded, center]);

  const fetchLegendColors = async () => {
    try {
      const res = await axiosInstance.get(`/api/surveyAnalytics/surveyColor/${survey?._id}`);
      const data = res?.data?.data || [];

      setLegendColors(data);
    } catch (err) {
      console.error("Failed to load legend colors:", err);
    }
  };

  useEffect(() => {
    if (!survey?._id) return;
    fetchLegendColors();
  }, [survey?._id]);

  const getLegendColor = (polygonName) => {
    if (!polygonName) return null;

    const nameUpper = polygonName.toString().trim().toUpperCase();

    const match = legendColors.find(
      (entry) => entry.location?.toString().trim().toUpperCase() === nameUpper
    );

    return match ? match.color : null;
  };

  // Polygon click handler
  const handlePolygonClick = (polygon) => {
    setSelectedPolygon(polygon);
    setSelectedLocationName(polygon.name);
    if (!window.google || !mapRefToUse.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    polygon.coordinate?.forEach((p) =>
      bounds.extend({ lat: Number(p.lat), lng: Number(p.lng) })
    );

    if (!bounds.isEmpty()) mapRefToUse.current.fitBounds(bounds);
  };

  const handleBackClick = () => {
    setSelectedPolygon(null);
    setSelectedLocationName(null);
    if (!window.google || !mapRefToUse.current) return;
    const bounds = new window.google.maps.LatLngBounds();

    polygonPaths?.forEach((poly) =>
      poly.coordinate?.forEach((point) => bounds.extend(point))
    );
    mapRef.current?.fitBounds(bounds);
  };

  const renderPolygons = () => {
    if (!polygonPaths || !Array.isArray(polygonPaths)) return null;
    if (!mapRefToUse.current || !window.google) return null;

    return polygonPaths.map((polygon, index) => {
      const bounds = new window.google.maps.LatLngBounds();
      // polygon.coordinate?.forEach((p) =>
      //   bounds.extend({ lat: Number(p.lat), lng: Number(p.lng) })
      // );
      polygon?.coordinate?.forEach((point) => bounds.extend(point));

      const isSelected = selectedPolygon?.name === polygon.name;

      const legendColor = getLegendColor(polygon.name)
    
      return (
        <React.Fragment key={index}>
          <Polygon
            paths={polygon?.coordinate}
            onClick={() => handlePolygonClick(polygon)}
            onLoad={handlePolygonLoad}
            options={{
              strokeColor: "#0D47A1",
              strokeOpacity: 0.9,
              strokeWeight: 2,
              fillColor: legendColor || "#3bbee8",
              fillOpacity: 0.6,
              zIndex: isSelected ? 1000 : 999,
            }}
          />
          {isSelected && (
          <InfoWindow
            // position={(() => {
            //   const bounds = new window.google.maps.LatLngBounds();
            //   selectedPolygon.coordinate?.forEach((p) =>
            //     bounds.extend({ lat: Number(p.lat), lng: Number(p.lng) })
            //   );
            //   return bounds.getCenter();
            // })()}
            position={bounds.getCenter()}
            onCloseClick={handleBackClick}
            options={{ maxWidth: 200 }}
          >
            <div className="bg-white/20 backdrop-blur-[30px] border border-white/40 p-3 rounded-lg">
                <div className="text-lg font-bold text-[#3bbee8] mb-2">
                  {selectedPolygon.name}
                </div>
                <div className="mt-2 flex justify-center">
                  <Button onClick={handleBackClick} size="small">
                    Back
                  </Button>
                </div>
              </div>
          </InfoWindow>
        )}
        </React.Fragment>
      );
    });
  };

  const onLoad = (map) => {
    if (!isLoaded) return;

    mapRefToUse.current = map;

    if (polygonPaths?.length) fitToPolygons(map, polygonPaths, center);
    else map.setCenter(center);
  };
  if (!isLoaded) return <div>Loading map...</div>;
  return (
    <div className={`${open ? "fixed inset-0 z-[9] h-[95%] w-[98%] mx-5 my-5" : ""}`}>
      
      {/* 🔹 SurveyAnalyticsDashboard overlay added */}
      {open && 
      <SurveyAnalyticsDashboard
        survey={survey}
        onLocationSelect={setSelectedLocationName}
        selectedLocationFromMap={selectedLocationName}
      />
      }

      {/* GOOGLE MAP */}
      <GoogleMap
        mapContainerClassName={`map-container w-full ${
          open ? "h-[95vh]" : "h-[500px]"
        }`}
        defaultCenter={center}
        defaultZoom={10}
        onLoad={onLoad}
        options={{ fullscreenControl: false }}
      >
        {renderPolygons()}
      </GoogleMap>

      {/* 🔹 Fullscreen close button */}
      {open && (
        <Button
          onClick={() => setIsFullScreen(false)}
          type="primary"
          danger
          className="absolute top-4 right-4 z-50 bg-white text-black"
        >
          Close
        </Button>
      )}

      {/* 🔹 SHOW + BUTTON IF NO SHAPE EXISTS */}
      {!survey?.isShapeExist && (
        <button
          onClick={() => setShowStateShape(true)}
          style={{
            position: "absolute",
            bottom: "50%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1677ff",
            color: "white",
            borderRadius: "50%",
            width: "65px",
            height: "65px",
            border: "none",
            fontSize: "30px",
            cursor: "pointer",
            zIndex: 50,
            boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
          }}
        >
          +
        </button>
      )}

      {/* 🔹 AddStateShape Modal */}
      <AddStateShape
        isOpen={showStateShape}
        id={id}
        setShowStateShape={setShowStateShape}
      />
    </div>
  );
};

export default SurveyMapView;


// import React, { useEffect, useRef, useState } from "react";
// import { GoogleMap, Polygon, InfoWindow } from "@react-google-maps/api";
// import { Button } from "antd";
// import AddStateShape from "../../SurveyAnalytics/AddStateShape";
// import SurveyAnalyticsDashboard from "./SurveyAnalyticsDasboard/SurveyAnalyticsDashboard";
// import useAxiosInstance from "../../../lib/useAxiosInstance";

// const SurveyMapView = ({
//   isLoaded,
//   open,
//   setIsFullScreen,
//   id,
//   center,
//   polygonPaths = [],
//   mapRef,
//   survey,
// }) => {
//   const axiosInstance = useAxiosInstance();

//   const internalRef = useRef(null);
//   const mapRefToUse = mapRef || internalRef;

//   const [legendColors, setLegendColors] = useState([]);
//   const [selectedPolygon, setSelectedPolygon] = useState(null);
//   const [selectedLocationName, setSelectedLocationName] = useState(null);
//   const [showStateShape, setShowStateShape] = useState(false);

//   // -----------------------------
//   // 🧠 LOCK SCROLL
//   // -----------------------------
//   useEffect(() => {
//     document.body.style.overflow = open ? "hidden" : "unset";
//     return () => (document.body.style.overflow = "unset");
//   }, [open]);

//   // -----------------------------
//   // 📍 FIT BOUNDS
//   // -----------------------------
//   const fitToPolygons = (mapInstance, polygons, fallbackCenter) => {
//     if (!window.google || !mapInstance || !polygons?.length) {
//       if (fallbackCenter) {
//         mapInstance?.setCenter(fallbackCenter);
//         mapInstance?.setZoom(10);
//       }
//       return;
//     }

//     const bounds = new window.google.maps.LatLngBounds();

//     polygons.forEach((polygon) => {
//       polygon?.path?.forEach((point) => bounds.extend(point));
//     });

//     if (!bounds.isEmpty()) {
//       mapInstance.fitBounds(bounds);
//     }
//   };

//   // -----------------------------
//   // 🎯 LEGEND COLORS
//   // -----------------------------
//   const fetchLegendColors = async () => {
//     try {
//       const res = await axiosInstance.get(
//         `/api/surveyAnalytics/surveyColor/${survey?._id}`
//       );
//       setLegendColors(res?.data?.data || []);
//     } catch (err) {
//       console.error("Legend error:", err);
//     }
//   };

//   useEffect(() => {
//     if (survey?._id) fetchLegendColors();
//   }, [survey?._id]);

//   const getLegendColor = (name) => {
//     if (!name) return null;

//     const match = legendColors.find(
//       (l) =>
//         l.location?.toUpperCase() === name.toUpperCase()
//     );

//     return match?.color || null;
//   };

//   // -----------------------------
//   // 🖱️ POLYGON CLICK
//   // -----------------------------
//   const handlePolygonClick = (polygon) => {
//     if (!polygon.name) return; // ignore constituency

//     setSelectedPolygon(polygon);
//     setSelectedLocationName(polygon.name);

//     if (!window.google || !mapRefToUse.current) return;

//     const bounds = new window.google.maps.LatLngBounds();
//     polygon.path?.forEach((p) => bounds.extend(p));

//     if (!bounds.isEmpty()) {
//       mapRefToUse.current.fitBounds(bounds);
//     }
//   };

//   // -----------------------------
//   // 🔙 BACK BUTTON
//   // -----------------------------
//   const handleBackClick = () => {
//     setSelectedPolygon(null);
//     setSelectedLocationName(null);

//     if (!window.google || !mapRefToUse.current) return;

//     const bounds = new window.google.maps.LatLngBounds();

//     polygonPaths.forEach((poly) =>
//       poly.path?.forEach((p) => bounds.extend(p))
//     );

//     mapRefToUse.current.fitBounds(bounds);
//   };

//   // -----------------------------
//   // 🔄 SYNC WITH DASHBOARD
//   // -----------------------------
//   useEffect(() => {
//     if (!selectedLocationName) return;

//     const match = polygonPaths.find(
//       (p) =>
//         p.name?.toUpperCase() === selectedLocationName.toUpperCase()
//     );

//     if (match) handlePolygonClick(match);
//   }, [selectedLocationName, polygonPaths]);

//   // -----------------------------
//   // 📍 AUTO FIT
//   // -----------------------------
//   useEffect(() => {
//     if (!isLoaded || !mapRefToUse.current) return;

//     const timer = setTimeout(() => {
//       fitToPolygons(mapRefToUse.current, polygonPaths, center);
//     }, 100);

//     return () => clearTimeout(timer);
//   }, [polygonPaths, isLoaded, center]);

//   // -----------------------------
//   // 🧱 RENDER POLYGONS
//   // -----------------------------
//   const renderPolygons = () => {
//     if (!polygonPaths || !window.google) return null;

//     return polygonPaths.map((polygon, index) => {
//       const isSelected = selectedPolygon?.name === polygon.name;
//       const legendColor = getLegendColor(polygon.name);

//       let strokeColor = "#000";
//       let strokeWeight = 1;
//       let fillColor = "transparent";
//       let fillOpacity = 0;

//       if (polygon.type === "block") {
//         strokeColor = "#000";
//         fillColor = legendColor || polygon.color || "#3bbee8";
//         fillOpacity = isSelected ? 0.7 : 0.4;
//       }

//       if (polygon.type === "village") {
//         strokeColor = "#666";
//         strokeWeight = 0.5;
//       }

//       if (polygon.type === "constituency") {
//         strokeColor = "#000";
//         strokeWeight = 3;
//       }

//       const bounds = new window.google.maps.LatLngBounds();
//       polygon.path?.forEach((p) => bounds.extend(p));

//       return (
//         <React.Fragment key={index}>
//           <Polygon
//             paths={polygon.path}
//             onClick={() => handlePolygonClick(polygon)}
//             options={{
//               strokeColor,
//               strokeWeight,
//               strokeOpacity: 1,
//               fillColor,
//               fillOpacity,
//               zIndex: isSelected ? 1000 : 1,
//             }}
//           />

//           {isSelected && polygon.name && (
//             <InfoWindow
//               position={bounds.getCenter()}
//               onCloseClick={handleBackClick}
//             >
//               <div>
//                 <strong>{polygon.name}</strong>
//                 <br />
//                 <Button size="small" onClick={handleBackClick}>
//                   Back
//                 </Button>
//               </div>
//             </InfoWindow>
//           )}
//         </React.Fragment>
//       );
//     });
//   };

//   // -----------------------------
//   // 🗺️ MAP LOAD
//   // -----------------------------
//   const onLoad = (map) => {
//     mapRefToUse.current = map;

//     if (polygonPaths?.length) {
//       fitToPolygons(map, polygonPaths, center);
//     } else {
//       map.setCenter(center);
//     }
//   };

//   if (!isLoaded) return <div>Loading map...</div>;

//   return (
//     <div className={`${open ? "fixed inset-0 z-[9]" : ""}`}>
      
//       {/* Dashboard */}
//       {open && (
//         <SurveyAnalyticsDashboard
//           survey={survey}
//           onLocationSelect={setSelectedLocationName}
//           selectedLocationFromMap={selectedLocationName}
//         />
//       )}

//       {/* MAP */}
//       <GoogleMap
//         mapContainerClassName={`w-full ${
//           open ? "h-[95vh]" : "h-[500px]"
//         }`}
//         defaultCenter={center}
//         defaultZoom={10}
//         onLoad={onLoad}
//         options={{ fullscreenControl: false }}
//       >
//         {renderPolygons()}
//       </GoogleMap>

//       {/* CLOSE BUTTON */}
//       {open && (
//         <Button
//           onClick={() => setIsFullScreen(false)}
//           className="absolute top-4 right-4 z-50"
//           danger
//         >
//           Close
//         </Button>
//       )}

//       {/* ADD SHAPE BUTTON */}
//       {!survey?.isShapeExist && (
//         <button
//           onClick={() => setShowStateShape(true)}
//           style={{
//             position: "absolute",
//             bottom: "50%",
//             left: "50%",
//             transform: "translateX(-50%)",
//             background: "#1677ff",
//             color: "white",
//             borderRadius: "50%",
//             width: "65px",
//             height: "65px",
//             border: "none",
//             fontSize: "30px",
//             cursor: "pointer",
//           }}
//         >
//           +
//         </button>
//       )}

//       <AddStateShape
//         isOpen={showStateShape}
//         id={id}
//         setShowStateShape={setShowStateShape}
//       />
//     </div>
//   );
// };

// export default SurveyMapView;